// CommitCalendar — visual month-grid picker for neighbor commits.
// Shows the next ~3 months of weekday cells. Already-committed weeks are
// shaded; the volunteer clicks an empty week and we surface a commit
// form pre-filled with that week's Saturday as the start date.

import { useMemo, useState } from 'react';

interface Commit {
  id: number;
  cadence: string;
  custom_cadence: string | null;
  preferred_day: string | null;
  start_date: string;
  end_date: string | null;
  status: string;
}

interface Props {
  commits: Commit[];                                    // existing approved/active
  monthsAhead?: number;                                 // default 3
  onClickWeek: (saturdayISO: string) => void;           // user clicked an open week
}

const WEEK_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] as const;
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'] as const;

function toISO(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function isCovered(date: Date, commits: Commit[]): boolean {
  const iso = toISO(date);
  for (const c of commits) {
    if (iso < c.start_date) continue;
    if (c.end_date && iso > c.end_date) continue;
    return true;
  }
  return false;
}

export function CommitCalendar({ commits, monthsAhead = 3, onClickWeek }: Props) {
  const [selected, setSelected] = useState<string | null>(null);

  const months = useMemo(() => {
    const out: { year: number; month: number; weeks: Date[][] }[] = [];
    const today = new Date();
    for (let m = 0; m < monthsAhead; m++) {
      const cursor = new Date(today.getFullYear(), today.getMonth() + m, 1);
      const start = startOfMonth(cursor);
      const end   = endOfMonth(cursor);
      const weeks: Date[][] = [];
      // Pad to start on Sunday.
      const padStart = start.getDay();
      let day = new Date(start);
      day.setDate(day.getDate() - padStart);
      let week: Date[] = [];
      while (day <= end || week.length > 0) {
        week.push(new Date(day));
        if (week.length === 7) { weeks.push(week); week = []; }
        day = new Date(day.getTime() + 86400000);
        if (day > end && week.length === 0) break;
      }
      out.push({ year: cursor.getFullYear(), month: cursor.getMonth(), weeks });
    }
    return out;
  }, [monthsAhead]);

  return (
    <div className="space-y-6">
      {months.map((m, mi) => (
        <div key={mi}>
          <h3 className="text-sm tracking-[0.2em] uppercase text-forest/60 mb-2">
            {MONTH_NAMES[m.month]} {m.year}
          </h3>
          <div className="grid grid-cols-7 gap-px bg-forest/10 border border-forest/15">
            {WEEK_LABELS.map((wl) => (
              <div key={wl} className="bg-cream-alt text-[10px] tracking-[0.15em] uppercase text-forest/55 text-center py-1">
                {wl}
              </div>
            ))}
            {m.weeks.map((wk, wi) => (
              wk.map((d) => {
                const inMonth = d.getMonth() === m.month;
                const covered = inMonth && isCovered(d, commits);
                const sat = d.getDay() === 6;
                const iso = toISO(d);
                const isSelected = selected === iso;
                const past = d < new Date(new Date().toDateString());
                const clickable = inMonth && !covered && !past && sat;
                return (
                  <button
                    key={iso}
                    type="button"
                    disabled={!clickable}
                    onClick={() => {
                      if (!clickable) return;
                      setSelected(iso);
                      onClickWeek(iso);
                    }}
                    className={`text-xs h-10 transition-colors ${
                      !inMonth || past
                        ? 'bg-cream-alt text-forest/30 cursor-not-allowed'
                        : covered
                        ? 'bg-forest/15 text-forest/55 cursor-not-allowed'
                        : isSelected
                        ? 'bg-rose text-cream'
                        : sat
                        ? 'bg-cream text-forest hover:bg-rose hover:text-cream cursor-pointer'
                        : 'bg-cream text-forest/35'
                    }`}
                    title={
                      !inMonth ? '' :
                      past ? 'past' :
                      covered ? 'already covered' :
                      sat ? 'available — click to claim' : ''
                    }
                  >
                    {inMonth ? d.getDate() : ''}
                  </button>
                );
                void wi;
              })
            ))}
          </div>
        </div>
      ))}
      <div className="text-xs text-forest/55 flex items-center gap-4">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 bg-cream border border-forest/20" /> available Saturday
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 bg-forest/15" /> already covered
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 bg-rose" /> selected
        </span>
      </div>
    </div>
  );
}
